/**
 * App Block Schema
 *
 * One App of an AppBase as a card on the edgeless canvas. The App Studio
 * structure overview projects every App mount of an AppBase into this block,
 * and the same block can carry an App into any other canvas.
 */

import type { GfxCompatibleProps } from '@blocksuite/std/gfx';
import { GfxCompatible } from '@blocksuite/std/gfx';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

export type AppBlockProps = {
  /** AppBase that holds the App. */
  appBaseId: string;
  /** Mount of the App inside the AppBase composition. */
  mountId: string;
  title: string;
  /** Route the App opens on. */
  routePath: string;
  /** The App currently provides the shared app frame of its AppBase. */
  isFrame: boolean;
  /** The App lies in the trash; its card holds its free place. */
  trashed: boolean;
} & GfxCompatibleProps;

export class AppBlockModel extends GfxCompatible<AppBlockProps>(BlockModel) {}

export const APP_BLOCK_WIDTH = 220;
export const APP_BLOCK_HEIGHT = 88;

export const AppBlockSchema = defineBlockSchema({
  flavour: 'affine:app',
  props: (): AppBlockProps => ({
    appBaseId: '',
    mountId: '',
    title: '',
    routePath: '',
    isFrame: false,
    trashed: false,
    xywh: `[0,0,${APP_BLOCK_WIDTH},${APP_BLOCK_HEIGHT}]`,
    index: 'a0',
    lockedBySelf: false,
  }),
  metadata: {
    role: 'content',
    version: 1,
    parent: ['affine:surface'],
    children: [],
  },
  toModel: () => new AppBlockModel(),
});

export const AppBlockSchemaExtension = BlockSchemaExtension(AppBlockSchema);
